/**
 * experiment_detail.ts
 *
 * Seamless preview-thumbnail transitions during HTMX polling. Each poll
 * replaces #experiment-status wholesale, so a changed slot would otherwise
 * paint its new image progressively, line by line. Instead:
 * - The new <img> starts at opacity 0 ([data-preview-img] CSS) while the
 *   previously displayed image is held as the slot's background-image.
 * - The new image preloads in the background; once FULLY loaded it fades in
 *   over the old one (.loaded → 250ms opacity transition).
 * - Reveals are staggered at most one per second, and each poll replaces the
 *   pending queue so only the latest images are shown.
 */

interface PendingSwap {
    index: number;
    newSrc: string;
}

(function (): void {
    let savedSlots: (string | null)[] = [];
    let pendingSwaps: PendingSwap[] = [];
    let swapTimer: ReturnType<typeof setTimeout> | null = null;

    function slotImg(slot: Element): HTMLImageElement | null {
        return slot.querySelector('[data-preview-img]');
    }

    function revealWhenLoaded(img: HTMLImageElement): void {
        if (img.complete && img.naturalWidth) {
            img.classList.add('loaded');
        } else {
            img.addEventListener('load', () => img.classList.add('loaded'), { once: true });
        }
    }

    // Server-rendered initial page — no swap event will reveal these.
    document.querySelectorAll('[data-preview-img]').forEach((img) => {
        revealWhenLoaded(img as HTMLImageElement);
    });

    // What the user can currently SEE in a slot: the <img> only once it has
    // been revealed — a mid-preload img (opacity 0) shows the slot background
    // (the previous image) instead. Using img.src here would let half-loaded
    // images be treated as "already displayed" and flash in unfinished.
    function visibleSrc(slot: HTMLElement): string | null {
        const img = slotImg(slot);
        if (img && img.classList.contains('loaded')) return img.getAttribute('src');
        const match = slot.style.backgroundImage.match(/url\(["']?(.*?)["']?\)/);
        return match ? match[1] : null;
    }

    function processNextSwap(): void {
        if (!pendingSwaps.length) {
            swapTimer = null;
            return;
        }

        const swap = pendingSwaps.shift()!;
        const slots = document.querySelectorAll('[data-preview-slot]');
        const slot = slots[swap.index] as HTMLElement | undefined;
        const img = slot ? slotImg(slot) : null;

        // Skip if a newer poll already replaced this slot's target — its own
        // afterSwap pass re-queued the slot with the newer src.
        if (slot && img && img.getAttribute('src') === swap.newSrc) {
            const reveal = () => {
                if (!img.parentNode) return;
                img.classList.add('loaded');
                setTimeout(() => {
                    slot.style.backgroundImage = '';
                }, 350);
            };
            if (img.complete && img.naturalWidth) {
                reveal();
            } else {
                const preloader = new Image();
                preloader.onload = reveal;
                preloader.src = swap.newSrc;
            }
        }

        swapTimer = setTimeout(processNextSwap, 1000);
    }

    document.body.addEventListener('htmx:beforeSwap', ((evt: CustomEvent) => {
        if (evt.detail.target.id !== 'experiment-status') return;
        savedSlots = [];
        document.querySelectorAll('[data-preview-slot]').forEach((slot) => {
            savedSlots.push(visibleSrc(slot as HTMLElement));
        });
    }) as EventListener);

    document.body.addEventListener('htmx:afterSwap', ((evt: CustomEvent) => {
        if (evt.detail.target.id !== 'experiment-status') return;
        const slots = document.querySelectorAll('[data-preview-slot]');

        const newPending: PendingSwap[] = [];
        slots.forEach((slot, i) => {
            const slotEl = slot as HTMLElement;
            const img = slotImg(slotEl);
            const newSrc = img ? img.getAttribute('src') : null;
            const oldSrc = savedSlots[i] || null;
            if (!img || !newSrc) return;

            if (oldSrc === newSrc) {
                // Same image as already displayed (browser-cached) — show it.
                revealWhenLoaded(img);
                return;
            }
            // Keep showing the old image (if any) while the new one preloads.
            if (oldSrc) slotEl.style.backgroundImage = 'url("' + oldSrc + '")';
            newPending.push({ index: i, newSrc });
        });

        // Replace the queue with the latest changes, processed in slot order.
        pendingSwaps = newPending;
        if (!swapTimer && pendingSwaps.length) processNextSwap();
        savedSlots = [];
    }) as EventListener);
})();

// Reveal HTMX-swapped panels.
//
// `[data-panel-fade-in]` panels start at `opacity: 0` (CSS) and are faded in once
// by a load-time GSAP intro (`initPanelFadeInAnimations`). Content swapped in by
// HTMX afterwards — the status poll's `#experiment-status`, plus the OOB gallery
// and results panels — never gets that intro pass, so it stays stuck invisible.
// That's why the progress/thumbnail panel "appears then goes away" on the first
// progress tick. Snap any still-hidden panel to visible after a swap settles.
document.body.addEventListener('htmx:afterSettle', (() => {
    document.querySelectorAll<HTMLElement>('[data-panel-fade-in]').forEach((el) => {
        el.style.opacity = '1';
        el.style.visibility = 'visible';
    });
}) as EventListener);

// Copy link button
document.addEventListener('DOMContentLoaded', () => {
    const btn = document.getElementById('copy-link-btn') as HTMLButtonElement | null;
    if (!btn) return;

    // The visible label lives in an inner <span> (keeps its padding/typography).
    // Update the span's text, not the button's, so we don't blow away the markup.
    // Exclude the tooltip span (role="tooltip") — it's also a direct child.
    const label = btn.querySelector('span:not([role="tooltip"])') as HTMLSpanElement | null;

    btn.addEventListener('click', () => {
        const url = window.location.href;

        const showCopied = () => {
            const target = label ?? btn;
            const original = target.textContent;
            target.textContent = 'Copied!';
            setTimeout(() => {
                target.textContent = original;
            }, 2000);
        };

        if (navigator.clipboard) {
            navigator.clipboard.writeText(url).then(showCopied).catch(() => {
                fallbackCopy(url);
                showCopied();
            });
        } else {
            fallbackCopy(url);
            showCopied();
        }
    });

    function fallbackCopy(text: string): void {
        const input = document.createElement('input');
        input.value = text;
        input.style.position = 'fixed';
        input.style.opacity = '0';
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
    }
});
